"use client";

import { useRef, useMemo, useEffect } from "react";
import { SeatTogglePayload } from "@/components/mapa/MapaViewer";
import { useEvento } from "@/store/evento/evento";
import { useEventoContext } from "../../contexts/eventos";
import { Funcion } from "@/types";

import { LayoutConMapa, LayoutSinMapa, ListadoFunciones } from ".";

const MAX_SEATS_PER_PURCHASE = 4;

export const SeleccionarFunciones = () => {
    const { evento, mapa } = useEventoContext();

    const { state: funcionSeleccionada, set: setFuncionSeleccionada } = useEvento.use.funcion();
    const { state: entradas, setAsiento, removeAsiento, clear } = useEvento.use.entradas();

    // 👉 ref al bloque donde arranca el mapa
    const mapaRef = useRef<HTMLDivElement | null>(null);

    // 1️⃣ FILTRO DE FECHAS CORREGIDO: Fusionando fecha y hora exacta
    const funcionesVigentes = useMemo(() => {
        if (!evento?.funciones) return [];

        const ahora = new Date();
        // Le restamos 2 horas a "ahora" para dar un changüí. 
        const limiteTolerancia = new Date(ahora.getTime() - (2 * 60 * 60 * 1000));

        return evento.funciones.filter((funcion: any) => {
            try {
                // Separamos la fecha pura (YYYY-MM-DD) por si viene con basura atrás
                const datePart = funcion.fecha.split('T')[0];
                // Agarramos la hora (HH:mm) o ponemos 00:00 por defecto
                const timePart = funcion.hora ? funcion.hora.slice(0, 5) : '00:00';

                // Armamos la fecha exacta del show: "2026-06-09T20:00:00"
                const fechaExactaStr = `${datePart}T${timePart}:00`;
                const fechaDeLaFuncion = new Date(fechaExactaStr);

                return fechaDeLaFuncion >= limiteTolerancia;
            } catch (error) {
                // Si alguna fecha viene rota de la BD, la mostramos por las dudas
                return true;
            }
        });
    }, [evento.funciones]);

    // 2️⃣ Auto-seleccionar la primera función DISPONIBLE Y VIGENTE si no hay ninguna seleccionada
    useEffect(() => {
        if (!funcionSeleccionada && funcionesVigentes.length > 0) {
            const primeraDisponible = funcionesVigentes.find(f => f.status !== 'agotado') || funcionesVigentes[0];
            setFuncionSeleccionada(primeraDisponible);
        }
    }, [funcionesVigentes, funcionSeleccionada, setFuncionSeleccionada]);

    const handleSeatToggle = (evt: SeatTogglePayload) => {
        if (!evt.tipoEntradaId) return;
        evt.selected ? setAsiento(evt) : removeAsiento(evt);
    };

    const handleSeleccion = (funcion: Funcion) => {
        clear();

        if (funcionSeleccionada?.id === funcion.id) return;

        setFuncionSeleccionada(funcion);

        if (typeof window === "undefined") return;

        // Pequeño delay para asegurar que el mapa esté montado la primera vez
        setTimeout(() => {
            if (!mapaRef.current) return;

            const rect = mapaRef.current.getBoundingClientRect();
            const absoluteY = rect.top + window.scrollY;

            // offset para que el mapa no quede pegado arriba
            const offset = window.innerHeight * 0.3; // ~30vh

            window.scrollTo({
                top: absoluteY - offset,
                behavior: "smooth",
            });
        }, 80);
    };

    const maxEntradas = useMemo(() => entradas.reduce((acc, entrada) => acc + (entrada.asientos?.length || 0), 0) >= MAX_SEATS_PER_PURCHASE, [entradas]);

    return (
        <section className="flex flex-col gap-4 w-full">
            {/* 3️⃣ Le pasamos SOLO las funciones vigentes al listado de botones */}
            <ListadoFunciones
                funciones={funcionesVigentes}
                onSeleccion={handleSeleccion}
                funcionSeleccionada={funcionSeleccionada}
            />

            {/* Contenedor referenciado para el scroll */}
            <div ref={mapaRef} className="w-full">
                {funcionSeleccionada && (
                    <div className="w-full">
                        {funcionSeleccionada.status === 'agotado' ? (
                            <div className="w-full bg-red-100 border-2 border-red-500 rounded-lg p-6 text-center">
                                <h3 className="text-xl font-display text-red-700 mb-2">SOLD OUT</h3>
                                <p className="text-red-600">
                                    Esta función ya no tiene entradas disponibles. Por favor, selecciona otra fecha.
                                </p>
                            </div>
                        ) : (
                            <>
                                {mapa ? (
                                    <LayoutConMapa
                                        funcionId={funcionSeleccionada.id}
                                        maxEntradas={maxEntradas}
                                        onSeatToggle={handleSeatToggle}
                                    />
                                ) : (
                                    <LayoutSinMapa
                                        funcionId={funcionSeleccionada.id}
                                        maxEntradas={maxEntradas}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};